// components/dashboard/StatusColumn.js
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Clock } from 'lucide-react';
import ComplaintCard from './ComplaintCard';

const StatusColumn = ({ column, complaints, onComplaintClick, onStatusUpdate, closedTimeRange, onClosedTimeRangeChange }) => {
  const [sortBy, setSortBy] = useState('newest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showTimeRangeMenu, setShowTimeRangeMenu] = useState(false);
  const sortMenuRef = useRef(null);
  const timeRangeMenuRef = useRef(null);

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'high-priority', label: 'High Priority First' },
    { value: 'low-priority', label: 'Low Priority First' }
  ];

  const timeRangeOptions = [
    { value: '24h', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'all', label: 'All time' }
  ];

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setShowSortMenu(false);
      }
      if (timeRangeMenuRef.current && !timeRangeMenuRef.current.contains(event.target)) {
        setShowTimeRangeMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('ring-2', 'ring-purple-400');
    const complaintId = e.dataTransfer.getData('complaintId');
    const currentStatus = e.dataTransfer.getData('currentStatus');
    
    if (currentStatus !== column.id) {
      onStatusUpdate(complaintId, column.id);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('ring-2', 'ring-purple-400');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('ring-2', 'ring-purple-400');
  };

  const getBackgroundColor = () => {
    switch(column.id) {
      case 'NEW':
        return 'bg-purple-50';
      case 'IN_PROGRESS':
        return 'bg-blue-50';
      case 'RESOLVED':
        return 'bg-green-50';
      case 'CLOSED':
        return 'bg-gray-50';
      default:
        return 'bg-gray-50';
    }
  };

  const getSortedComplaints = () => {
    const complaintsCopy = [...complaints];
    
    switch(sortBy) {
      case 'newest':
        return complaintsCopy.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at);
          const dateB = new Date(b.createdAt || b.created_at);
          return dateB - dateA;
        });
      
      case 'oldest':
        return complaintsCopy.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at);
          const dateB = new Date(b.createdAt || b.created_at);
          return dateA - dateB;
        });
      
      case 'high-priority':
        return complaintsCopy.sort((a, b) => {
          const priorityOrder = { 'URGENT': 0, 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3 };
          const priorityA = priorityOrder[a.priority?.toUpperCase()] ?? 4;
          const priorityB = priorityOrder[b.priority?.toUpperCase()] ?? 4;
          return priorityA - priorityB;
        });
      
      case 'low-priority':
        return complaintsCopy.sort((a, b) => {
          const priorityOrder = { 'LOW': 0, 'MEDIUM': 1, 'HIGH': 2, 'URGENT': 3 };
          const priorityA = priorityOrder[a.priority?.toUpperCase()] ?? 4;
          const priorityB = priorityOrder[b.priority?.toUpperCase()] ?? 4;
          return priorityA - priorityB;
        });
      
      default:
        return complaintsCopy;
    }
  };

  const sortedComplaints = getSortedComplaints();
  const currentSortLabel = sortOptions.find(opt => opt.value === sortBy)?.label || 'Newest First';
  const currentTimeRangeLabel = timeRangeOptions.find(opt => opt.value === closedTimeRange)?.label || 'Last 24 hours';

  return (
    <div 
      className={`${getBackgroundColor()} rounded-lg shadow-sm border border-gray-200 flex flex-col h-full transition-all`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Column Header */}
      <div className="p-4 bg-white border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{column.title}</h3>
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
              {complaints.length}
            </span>
          </div>
          
          {/* Time Range Filter (only for CLOSED) */}
          {column.id === 'CLOSED' && onClosedTimeRangeChange && (
            <div className="relative" ref={timeRangeMenuRef}>
              <button
                onClick={() => setShowTimeRangeMenu(!showTimeRangeMenu)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
              >
                <Clock className="w-3 h-3" />
                <span>{currentTimeRangeLabel}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              
              {showTimeRangeMenu && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  {timeRangeOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        onClosedTimeRangeChange(option.value);
                        setShowTimeRangeMenu(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                        closedTimeRange === option.value ? 'text-purple-600 bg-purple-50' : 'text-gray-700'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Sort Dropdown */}
        <div className="relative" ref={sortMenuRef}>
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            <span>Sorted by: {currentSortLabel}</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          
          {showSortMenu && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              {sortOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSortBy(option.value);
                    setShowSortMenu(false);
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    sortBy === option.value ? 'text-purple-600 bg-purple-50' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Column Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {sortedComplaints.length > 0 ? (
          sortedComplaints.map((complaint) => (
            <ComplaintCard
              key={complaint.id}
              complaint={complaint}
              onClick={onComplaintClick}
              onStatusUpdate={onStatusUpdate}
            />
          ))
        ) : (
          <div className="text-center py-8 text-gray-400">
            <p className="text-sm">No tickets in this status</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusColumn;